import React, { useState, useEffect } from "react";
import { showToastNotification } from "@/utility/helper";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import FormField from "@/components/common/FormField";
import type {
  IBulkImportPreviewRow,
  IBulkImportRow,
} from "@/interface/bulkImport.interface";
import {
  getDisplayHeaders,
  getFieldValue,
  normalizeFieldKey,
} from "./bulkImport.utils";

interface EditRowModalProps {
  open: boolean;
  onClose: () => void;
  row: IBulkImportPreviewRow | null;
  headers: string[];
  onSave: (rowIndex: number, updatedData: IBulkImportRow) => void;
}

/**
 * Modal to edit a single row from the bulk import preview.
 * Shows each field as an input, highlights errored fields with red borders.
 */
const EditRowModal: React.FC<EditRowModalProps> = ({
  open,
  onClose,
  row,
  headers,
  onSave,
}) => {
  const [formData, setFormData] = useState<IBulkImportRow>(
    row?.data ? { ...row.data } : {}
  );

  // Sync form data whenever the modal opens or the row changes
  useEffect(() => {
    if (open && row?.data) {
      setFormData({ ...row.data });
    }
  }, [open, row]);

  if (!row) return null;

  // Compute headers to display (fallback to row keys if headers array is empty)
  const displayHeaders = getDisplayHeaders(headers, row.data);

  // Build dynamic map of errors based on current formData vs original row.data
  const errorMap = new Map<string, string>();
  row.errors.forEach((err) => {
    const keyLower = err.field.toLowerCase().trim();
    const origVal = String(getFieldValue(row.data, err.field) ?? "").trim();
    const currVal = String(getFieldValue(formData, err.field) ?? "").trim();
    if (!currVal) {
      errorMap.set(keyLower, err.message || `${err.field} is required`);
    } else if (currVal === origVal) {
      // User hasn't changed the original value that caused the error
      errorMap.set(keyLower, err.message);
    }
  });

  // Check essential required fields that must never be empty
  const requiredHeaders = ["name", "email", "phone", "gender", "employeecode", "status", "type"];
  displayHeaders?.forEach((header) => {
    const keyLower = header.toLowerCase().trim();
    const currVal = String(getFieldValue(formData, header) ?? "").trim();
    if (!currVal && requiredHeaders.includes(keyLower)) {
      if (!errorMap.has(keyLower)) {
        errorMap.set(keyLower, `${header} is required`);
      }
    }
  });

  const displayHeaderSetLower = new Set(
    displayHeaders.map((h) => h.toLowerCase().trim())
  );
  const generalErrors = row.errors.filter(
    (e) =>
      !displayHeaderSetLower.has(e.field.toLowerCase().trim()) ||
      e.field.toLowerCase() === "general"
  );

  const handleFieldChange = (header: string, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [header]: value };
      const headerLower = header.toLowerCase().trim();
      const headerNorm = normalizeFieldKey(header);
      Object.keys(prev).forEach((key) => {
        if (
          key.toLowerCase().trim() === headerLower ||
          normalizeFieldKey(key) === headerNorm
        ) {
          updated[key] = value;
        }
      });
      return updated;
    });
  };

  const handleSave = () => {
    if (errorMap.size > 0) {
      showToastNotification("danger", "Please fix all highlighted errors and fill required fields before saving.");
      return;
    }
    onSave(row.rowIndex, formData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-800">
            Edit Row #{row.rowIndex}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Fix the highlighted fields and save to mark this row as valid.
          </DialogDescription>
        </DialogHeader>

        {generalErrors?.length > 0 && (
          <div className="p-3 my-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
            <span className="font-bold block mb-1">Row Errors:</span>
            <ul className="list-disc pl-4 space-y-1">
              {generalErrors.map((ge, idx) => (
                <li key={idx}>
                  {ge.field !== "General" && (
                    <span className="font-semibold">{ge.field}: </span>
                  )}
                  {ge.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          {displayHeaders?.map((header) => {
            const errorMessage = errorMap.get(header.toLowerCase().trim());
            const hasError = !!errorMessage;
            const fieldValue = getFieldValue(formData, header);
            return (
              <div key={header}>
                <FormField
                  label={header}
                  type="text"
                  value={String(fieldValue ?? "")}
                  onChange={(e) => handleFieldChange(header, e.target.value)}
                  error={errorMessage}
                  className={hasError ? "border-red-400 focus:ring-red-200" : ""}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRowModal;
